import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import Skeleton from 'react-loading-skeleton';
import { getMessageThreads } from '../../services/messageThreads';
import AccountNav from '../../components/customerArea/shared/AccountNav';
import MessageThreadsList from '../../components/customerArea/messages/MessageThreadsList';
import Row from '../../components/shared/atoms/Row';
import Box from '../../components/shared/atoms/Box';
import Container from '../../components/shared/atoms/Container';
import Heading from '../../components/shared/atoms/Heading';

const ErrorBox = styled.div`
  padding: 14px;
  margin: 16px 0;
  background: ${(p) => p.theme.colors.red.pale};
  color: ${(p) => p.theme.colors.red.dark};
  border-radius: 8px;
  font-size: 14px;
`;

const MessagesSkeleton = () => (
  <Box data-testid="messages-skeleton">
    <Skeleton style={{ borderRadius: 8 }} height={110} />
    <Skeleton style={{ borderRadius: 8, marginTop: 24 }} height={110} />
  </Box>
);

/**
 * FIXERLIGHT-049 — « Mes messages », même famille et mêmes patrons que
 * « Mes rendez-vous d'atelier » (FIXERLIGHT-052) : accessible depuis
 * l'espace client via `AccountNav`, même geste de chargement/erreur/garde
 * de connexion.
 *
 * Route `/messages` (racine du site, PAS `/account/...`) — imposé par le
 * détail (`/messages/:id`, contrat api § 6, lien de l'email de réponse) ;
 * gardée ici par cohérence avec sa liste.
 */
function Messages() {
  const { t } = useTranslation();
  const client = useSelector((s) => s.client);
  const history = useHistory();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!client.id) {
      history.push(`/login${window.location.search}`);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await getMessageThreads(client.id);
        if (cancelled) return;
        setThreads(data);
      } catch (e) {
        if (cancelled) return;
        setError(true);
        setThreads([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  return (
    <Container>
      <Container fluid pb={10} pt={4}>
        <Helmet
          title={t('messages.meta.title')}
          meta={[{ name: 'robots', content: 'noindex, nofollow' }]}
        />
        <Row md gap={56}>
          <AccountNav />
          <Box mt={{ xs: -7, lg: 0 }} style={{ flex: 1 }}>
            <Heading level={2} mb={{ xs: 4, lg: 6 }}>
              {t('messages.title')}
            </Heading>
            {loading ? (
              <MessagesSkeleton />
            ) : error ? (
              <ErrorBox data-testid="messages-error">
                {t('messages.error')}
              </ErrorBox>
            ) : (
              <MessageThreadsList threads={threads} />
            )}
          </Box>
        </Row>
      </Container>
    </Container>
  );
}

export default Messages;
